import { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import Modal from "components/Modal/Modal"
import Button from "components/Button/Button"
import { Flex } from "components/Flex"
import toastMessages from "stores/toastMessages"
import ShippingOrderAssetExtended from "models/ShippingOrderAssetExtended"
import { Guid } from "@/types"
import { Grid, GridItem } from "components/Grid"
import { IGridProps } from "components/Grid/Grid"
import Checkbox from "components/Checkbox/Checkbox"
import Lmid from "components/Lmid/Lmid"
import { useTenantSettings } from "stores/tenantSettings"
import NoValue from "components/NoValue/NoValue"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"
import Text from "components/Text/Text"

interface IUpdateHomebaseModalProps {
    assets: ShippingOrderAssetExtended[]
    onSave(assetsToUpdate: ShippingOrderAssetExtended[]): Promise<void>
    onClose(): void
}

const UpdateHomebaseModal: FC<IUpdateHomebaseModalProps> = ({
    assets,
    onSave,
    onClose,
}) => {
    const { t } = useTranslation()
    const [isSaving, setIsSaving] = useState(false)
    const [selectedAssetIds, setSelectedAssetIds] = useState<Guid[]>(assets.map(a => a.assetId))
    const { tenantWarehouses, fetchTenantWarehouses } = useTenantSettings()

    useEffect(() => {
        fetchTenantWarehouses()
    }, [fetchTenantWarehouses])

    const gridProps: IGridProps = {
        columns: "max-content 220fr 306fr 306fr",
        gap: "3.2rem",
        alignItems: "center",
    }

    async function handleSave() {
        setIsSaving(true)
        try {
            await onSave(assets.filter(a => selectedAssetIds.includes(a.assetId)))
            onClose()
        } catch(e) {
            toastMessages.showSystemError()
            setIsSaving(false)
        }
    }

    function toggleAssetSelection(assetId: Guid) {
        setSelectedAssetIds(current => {
            if (current.includes(assetId)) {
                return current.filter(id => id !== assetId)
            } else {
                return [...current, assetId]
            }
        })
    }

    function toggleAllSelection() {
        if (selectedAssetIds.length === assets.length) {
            setSelectedAssetIds([])
        } else {
            setSelectedAssetIds(assets.map(a => a.assetId))
        }
    }

    return (
        <Modal
            title={t("updateHomebase.updateHomebase")}
            maxWidth="95.2rem"
            onClose={onClose}
            footer={
                <Flex justifyContent="flex-end">
                    <Button
                        label={t('common.save')}
                        onClick={handleSave}
                        disabled={isSaving}
                        loading={isSaving}
                    />
                </Flex>
            }>
            <Flex flow="column" gap="3.2rem" className="overflow-hidden max-h-100">
                <Text preLine>
                    {t("updateHomebase.updateHomebaseDescr")}
                </Text>
                <div className="overflow-auto">
                    <Grid {...gridProps} className="color-grey-500 font-bold py-1 bg-white sticky-top z-index-2">
                        <GridItem justifySelf="center" className="pl-2">
                            <Checkbox
                                checked={selectedAssetIds.length === assets.length}
                                partiallyChecked={selectedAssetIds.length > 0 && selectedAssetIds.length < assets.length}
                                onChange={toggleAllSelection}
                            />
                        </GridItem>
                        <GridItem>
                            {t("common.lmid")}
                        </GridItem>
                        <GridItem>
                            {t("updateHomebase.currentHomebase")}
                        </GridItem>
                        <GridItem>
                            {t("updateHomebase.newHomebase")}
                        </GridItem>
                    </Grid>
                    {assets.map(asset => (
                        <Grid {...gridProps} key={asset.assetId}
                            className={`py-2 border-top-grey-100${selectedAssetIds.includes(asset.assetId) ? ' bg-grey-50' : ''}`}>
                            <GridItem justifySelf="center" className="pl-2">
                                <Checkbox
                                    checked={selectedAssetIds.includes(asset.assetId)}
                                    onChange={() => toggleAssetSelection(asset.assetId)}
                                />
                            </GridItem>
                            <GridItem>
                                <Lmid
                                    lmid={asset.lmid}
                                    assetId={asset.assetId}
                                    labeled={asset.isLabeled}
                                />
                            </GridItem>
                            <GridItem>
                                {tenantWarehouses
                                    ? (asset.homebaseId
                                        ? <FlexibleContent text={
                                            asset.homebaseId === tenantWarehouses.noWarehouseId
                                                ? t("common.noWarehouse")
                                                : tenantWarehouses.entities.find(w => w.id === asset.homebaseId)?.description || ""
                                            } />
                                        : <NoValue />
                                    )
                                    : <NoValue>{t("common.loading")}</NoValue>
                                }
                            </GridItem>
                            <GridItem>
                                {tenantWarehouses
                                    ? asset.arrivalLocation ? <FlexibleContent text={asset.arrivalLocation.description} /> : <NoValue />
                                    : <NoValue>{t("common.loading")}</NoValue>
                                }
                            </GridItem>
                        </Grid>
                    ))}
                </div>
            </Flex>
        </Modal>
    )
}

export default UpdateHomebaseModal
